<template>
  <div class="PassengersCardContent">
    <div class="PassengersCardContent-interactions">
      <template v-if="isTabletOrDesktop">
        <ActionButton
          variant="tertiary"
          size="sm"
          leftIcon="pencil"
          @click="edit"
        >
          {{ t('ui.components.passengersCard.edit') }}
        </ActionButton>

        <Tooltip :disabled="passenger.isDeletable" position="left">
          <template #trigger>
            <ActionButton
              variant="tertiary"
              size="sm"
              leftIcon="delete"
              :disabled="!passenger.isDeletable"
              @click="remove"
            >
              {{ t('ui.components.passengersCard.remove') }}
            </ActionButton>
          </template>

          <p>{{ t('ui.components.passengersCard.deleteTooltip', { date: formattedDeletableDate }) }}</p>
        </Tooltip>
      </template>

      <template v-else>
        <IconButton
          icon="pencil"
          size="sm"
          color="secondary"
          :ariaLabel="t('ui.components.passengersCard.edit')"
          @click="edit"
        />

        <Tooltip :disabled="passenger.isDeletable" position="left">
          <template #trigger>
            <IconButton
              icon="delete"
              size="sm"
              color="secondary"
              :ariaLabel="t('ui.components.passengersCard.remove')"
              :disabled="!passenger.isDeletable"
              @click="remove"
            />
          </template>

          <p>{{ t('ui.components.passengersCard.deleteTooltip', { date: formattedDeletableDate }) }}</p>
        </Tooltip>
      </template>
    </div>

    <PassengerFields :fields />
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { useI18n } from '@cvo/i18n/composables/useI18n';
import ActionButton from '@cvo/ui/components/atoms/ActionButton/ActionButton.vue';
import IconButton from '@cvo/ui/components/atoms/IconButton/IconButton.vue';
import Tooltip from '@cvo/ui/components/atoms/Tooltip/Tooltip.vue';
import PassengerFields from '@cvo/ui/components/molecules/PassengerFields/PassengerFields.vue';
import { useResponsive, useDateFormat } from '@cvo/ui/composables';
import type { PassengersCardPassenger } from './PassengersCard.model';

const props = defineProps<{ passenger: PassengersCardPassenger }>();

const emits = defineEmits<{
  edit: [passengerId: string];
  remove: [passengerId: string];
}>();

const { t } = useI18n();
const { isTabletOrDesktop } = useResponsive();

const birthDate = computed(() => (props.passenger.dateOfBirth ? new Date(props.passenger.dateOfBirth) : null));
const formattedDateOfBirth = useDateFormat(birthDate);
const deletableAt = computed(() => (props.passenger.deletableAt ? new Date(props.passenger.deletableAt) : null));
const formattedDeletableDate = useDateFormat(deletableAt, { dateFormat: 'long', timeFormat: 'short' });

const fields = computed<{ label: string; value: string }[]>(() => [
  { label: t('ui.form.fields.salutation.label'), value: props.passenger.salutation ?? '' },
  { label: t('ui.form.fields.firstName.label'), value: props.passenger.firstName },
  { label: t('ui.form.fields.lastName.label'), value: props.passenger.lastName },
  { label: t('ui.person.details.birthDate.title'), value: formattedDateOfBirth.value },
  { label: t('ui.person.details.gender.title'), value: t(`ui.person.details.gender.options.${props.passenger.gender}`) },
  { label: t('ui.form.fields.nationality.label'), value: props.passenger.nationality ?? '' },
]);

function edit() {
  emits('edit', props.passenger.id);
}

function remove() {
  emits('remove', props.passenger.id);
}
</script>

<style lang="scss">
@use '@cvo/ui/styles/design-system' as vars;

@layer cvo-ui {
  .PassengersCardContent {
    display: flex;
    flex-direction: column;
    gap: vars.$dimensions-d-6;

    &-interactions {
      display: flex;
      justify-content: flex-end;
      gap: vars.$dimensions-d-2;
      padding-block: vars.$dimensions-d-1;
      border-bottom: vars.$molecules-accordion-border-stroke solid vars.$molecules-accordion-border-color;

      .IconButton {
        padding: vars.$dimensions-d-2;
      }
    }
  }
}
</style>
